import { girthFromLandmark, Vec3 } from "../measure/landmarkGirth";
import { measurements as landmarks } from "../measure/measurePoints";
import { getMeasurementFromModel } from "../measure/measureCache";

/** Vertex positions from OBJ text. Cheap: the mesh is already in memory. */
const positionsFromObj = (obj: string): Vec3[] =>
    obj
        .split("\n")
        .filter((l) => l.startsWith("v "))
        .map((l) => {
            const p = l.split(/\s+/);
            return [Number(p[1]), Number(p[2]), Number(p[3])] as Vec3;
        });

/**
 * Measures the narrowest waist on a mesh. Injected so the arithmetic above it
 * stays testable: the real implementation loads a THREE mesh and cannot run in
 * the node environment the unit tests use.
 */
export type MeshMeasurer = (model: {
    obj: string;
    sessionID: string;
    inputHeightCm?: number;
}) => Promise<{ best: { slice: { hullLength: number } }; scale?: number }>;

/**
 * The projected waist, by the same definition the client's own waist uses.
 *
 * A male waist is taken over the navel and a female waist is the narrowest
 * circumference — different measurements, not two implementations of one, so
 * each is reproduced by its own method rather than one standing in for both.
 */
const projectWaistCm = async (
    obj: string,
    gender: "male" | "female",
    heightCm: number | undefined,
    targetBodyFat: number,
    measureMesh: MeshMeasurer
): Promise<number | null> => {
    if (!heightCm) return null;

    if (gender === "male") {
        const waist = landmarks.find((m) => m.name === "waist");
        if (!waist?.vertices || !waist.plane) return null;
        return girthFromLandmark(
            positionsFromObj(atob(obj)),
            { vertices: waist.vertices, plane: waist.plane as [number, number, number] },
            heightCm
        );
    }

    try {
        const res = await measureMesh({
            obj,
            // A distinct key on purpose. measureCache keys on sessionID alone,
            // so measuring a generated twin under the scan's own session id
            // would hand back the goal figures where the real ones belong.
            sessionID: `goal:${targetBodyFat}`,
            inputHeightCm: heightCm,
        });
        return res.best.slice.hullLength * (res.scale || 1);
    } catch {
        // A waist that cannot be measured is absent, not zero.
        return null;
    }
};

export interface GoalProjection {
    /** Percent, e.g. 16. */
    targetBodyFat: number;
    /**
     * Lean mass held, and the weight and fat mass solved from it. Absent for a
     * client who has never been weighed: the body and its waist need only the
     * target, but kilograms need a weight to start from.
     */
    leanHeldKg?: number;
    weightKg?: number;
    fatKg?: number;
    /** The generated mesh, as OBJ text. */
    obj: string;
    /** Measured off the generated mesh. Null when it cannot be derived. */
    waistCm: number | null;
}

/** The slice of ClientDataHolder this module needs. */
export interface GoalMeshSource {
    getEstimation: (fraction: number) => Promise<string>;
}

export interface GoalSubject {
    gender: "male" | "female";
    heightCm?: number;
}

// One cache per source, which is one per client. Keyed on the target alone it
// was shared across the tab, so a coach who set 25 % for one client and then
// opened another at 25 % was shown the first client's body.
let caches = new WeakMap<GoalMeshSource, Map<string, Promise<GoalProjection>>>();

export const clearGoalProjectionCache = () => {
    caches = new WeakMap();
};

/**
 * The client's twin at their target body fat.
 *
 * Weight follows from holding lean mass: if lean is (1 - target) of the whole,
 * then the whole is lean / (1 - target). The mesh comes from the API, and
 * measuring it — with the scan pipeline's own code — is what makes a projected
 * waist defensible rather than invented.
 *
 * The mesh and the waist need only the target. Without lean mass — a client
 * who has never been weighed — the body is still built and measured, and the
 * kilograms are left out rather than guessed.
 */
export const projectGoal = (
    source: GoalMeshSource,
    targetBodyFat: number,
    leanHeldKg: number | undefined,
    subject?: GoalSubject,
    measureMesh: MeshMeasurer = getMeasurementFromModel as MeshMeasurer
): Promise<GoalProjection> => {
    if (!(targetBodyFat > 0 && targetBodyFat < 100)) {
        return Promise.reject(
            new Error(`target body fat out of range: ${targetBodyFat}`)
        );
    }

    let cache = caches.get(source);
    if (!cache) caches.set(source, (cache = new Map()));
    // Lean is in the key because weight is solved from it: a new scan moves it.
    // A client never weighed has none, and shares one entry per target.
    const key = `${targetBodyFat}:${leanHeldKg ?? "none"}`;
    const cached = cache.get(key);
    if (cached) return cached;

    const pending = source
        .getEstimation(targetBodyFat / 100)
        .then(async (obj) => {
            const weightKg =
                leanHeldKg === undefined
                    ? undefined
                    : leanHeldKg / (1 - targetBodyFat / 100);
            return {
                targetBodyFat,
                leanHeldKg,
                weightKg,
                fatKg:
                    weightKg === undefined || leanHeldKg === undefined
                        ? undefined
                        : weightKg - leanHeldKg,
                obj,
                waistCm: subject
                    ? await projectWaistCm(
                          obj,
                          subject.gender,
                          subject.heightCm,
                          targetBodyFat,
                          measureMesh
                      )
                    : null,
            };
        })
        .catch((err) => {
            // A failed generation must not poison the cache, or the column stays
            // broken until reload.
            cache!.delete(key);
            throw err;
        });

    cache.set(key, pending);
    return pending;
};
